import React from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useHistory } from "react-router-dom";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash.throttle";
import { useLoadScript } from "@react-google-maps/api";

import { libraries } from "../helper";
import useGoogleMap from "./useGoogleMap";

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

export default function PlacesAuto({
  noMap,
  refine,
  value,
  setValue,
  children,
  className,
}) {
  const classes = useStyles();
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [mapInstance] = useGoogleMap();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
    libraries: libraries,

    // ...otherOptions
  });
  const history = useHistory();

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: { country: "us" },
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [isLoaded, value, inputValue, fetch]);

  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option.structured_formatting.main_text
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      className={className}
      freeSolo
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        if (newValue && !newValue.place_id && options.length >= 1) {
          newValue = options[0];
        }

        if (mapInstance && !noMap && newValue && newValue.place_id) {
          mapInstance.places.getDetails(
            {
              fields: ["geometry"],
              placeId: newValue.place_id,
            },
            (result) => {
              if (!result) return;
              let bounds = result.geometry.viewport;
              if (mapInstance && mapInstance.map) {
                mapInstance.map.fitBounds(bounds);
              }
              if (bounds) {
                refine({
                  northEast: bounds.getNorthEast().toJSON(),
                  southWest: bounds.getSouthWest().toJSON(),
                });
              }
            }
          );
        }
        let text =
          newValue && newValue.structured_formatting
            ? newValue.structured_formatting.main_text
            : newValue;
        history.push(`/search/${text}`, {
          state: { option: newValue },
          replace: true,
        });
        setValue(newValue);
      }}
      popupIcon={null}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => {
        let { InputLabelProps, InputProps, ...rest } = params;
        rest = { ...rest, ...InputProps };
        return React.cloneElement(children, rest);
      }}
      renderOption={(option) => {
        if (!option.structured_formatting) return null;
        const matches =
          option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}
